import type { SupplierHandoff } from '../supplierExchange/types';

/**
 * Carrying a handoff into a new CG order.
 *
 * The shape travels in the router's location.state, deliberately not in query
 * params. `client` is an end-customer name: a URL is copied into chat, pasted
 * into tickets, and kept in browser history and server logs, and none of those
 * are places a client list should end up. location.state stays in memory.
 *
 * Everything here is a SUGGESTION. The order form is fully editable
 * afterwards, and the only field the flow insists on is the one it cannot
 * know — the Shopify order id, which is where it puts the cursor.
 */

export interface OrderPrefill {
  /** Stamped with handoff_po_id once the order exists. */
  handoffId: string;
  client: string;
  /** ISO date, from the supplier's acknowledgement. Null if somehow unset. */
  dateAcknowledged: string | null;
  /** ISO date. Today — the PO is being sent now, which is the point. */
  datePoSent: string;
  /** Raw text off the handoff; matched against the real list at render time. */
  productType: string;
  description: string;
  /** Blank rather than a guessed number when the handoff did not say. */
  units: string;
}

/** The date part of a timestamp, in the form a date input wants. */
function dateOnly(timestamp: string | null): string | null {
  if (timestamp === null || timestamp === '') {
    return null;
  }
  const [date] = timestamp.split('T');
  return date === undefined || date === '' ? null : date;
}

export function buildOrderPrefill(handoff: SupplierHandoff, today: string): OrderPrefill {
  return {
    handoffId: handoff.id,
    client: handoff.client,
    dateAcknowledged: dateOnly(handoff.acknowledged_at),
    datePoSent: today,
    productType: handoff.product_type,
    description: handoff.description,
    units: handoff.units === null ? '' : String(handoff.units),
  };
}

/**
 * The handoff's free-text product type, resolved against the order form's
 * actual list.
 *
 * Free text on one side and a foreign key on the other is the price of the
 * module boundary: whoever sends a handoff may not be allowed to read
 * cg_product_types at all. Matching is case- and space-insensitive, and an
 * unrecognised value returns '' so the picker simply stays unset — a wrong
 * product type selected confidently is worse than an empty one.
 */
export function matchProductType(raw: string, known: string[]): string {
  const needle = raw.trim().toLowerCase();
  if (needle === '') {
    return '';
  }
  return known.find((candidate) => candidate.trim().toLowerCase() === needle) ?? '';
}

/**
 * Whether a handoff can start this flow at all.
 *
 * Acknowledged, and not already turned into an order. Both halves matter: a
 * PO for a design nobody confirmed they can make is a promise you cannot
 * keep, and a second order from the same handoff would silently replace the
 * first order's link.
 */
export function canIssuePo(handoff: SupplierHandoff): boolean {
  return handoff.acknowledged_at !== null && handoff.handoff_po_id === null;
}
